import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { StatsCollector, STATS_SOURCE, STATS_DEVICE_TYPE, STATS_LOCATION } from "zstats";
import { getSelectedHotel } from "actions/hotel";
import { getToken, getApiUrl } from "actions/config";
import { getToken as getUserToken } from "actions/user";

const StatsContext = createContext(null);

const SNAPSHOT_KEY = (hotelRef) => `zstats.snapshot.${hotelRef}`;
const TOMBSTONE_KEY = (hotelRef) => `zstats.tombstone.${hotelRef}`;

const makeId = () => Math.random().toString(36).slice(2);
const nowStr = () => new Date().toISOString();

const SID_KEY = (projectRef) => `zstats.sessionId.${projectRef}`;

const readAndConsumeSessionId = (projectRef) => {
    try {
        const raw = localStorage.getItem(SID_KEY(projectRef));
        if (!raw) return null;
        localStorage.removeItem(SID_KEY(projectRef));
        const { id } = JSON.parse(raw);
        return id || null;
    } catch {
        return null;
    }
};

const writeSessionId = (projectRef, id) => {
    try {
        localStorage.setItem(SID_KEY(projectRef), JSON.stringify({ id }));
    } catch {}
};

export const persistStats = ({ stats, eventName, alsoFlush = false }) => {
    if (!stats) return;
    try {
        if (eventName) stats.endDurationEvent?.({ eventName });
        stats.saveSnapshot?.();
        writeSessionId(stats.projectRef, stats.sessionId);
        if (alsoFlush) stats.flush?.({ endEvents: false });
    } catch {}
};
export function StatsProvider({ children }) {
    const selectedRooms = useSelector((s) => s?.session?.selectedRooms || {});
    const selectedRoomsId = useSelector((s) => s?.session?.selectedRoomId || {});
    const project = useSelector(getSelectedHotel);
    const projectRef = project?.ref || "default";
    const selectedRoom = selectedRooms?.[projectRef];
    const selectedRoomId = selectedRoomsId?.[projectRef] || null;
    const userToken = useSelector(getUserToken);
    const appToken = useSelector(getToken);
    const token = userToken ? userToken : appToken;
    const servicePath = useSelector(getApiUrl) || getApiUrl();
    const uid = useSelector((s) => s.identity.uid);
    const appUID = useSelector((s) => s.identity.appUID);
    const guestId = useSelector((s) => s.session?.guestID);
    const deviceRef = `${appUID}:${uid}`;

    const statsRef = useRef(null);
    const [stats, setStats] = useState(null);

    const instanceIdRef = useRef(null);
    const flushedAtRef = useRef(0);
    const projectRefRef = useRef(projectRef);
    useEffect(() => {
        projectRefRef.current = projectRef;
    }, [projectRef]);

    console.log(localStorage.getItem(`zstats.snapshot.${projectRef}`));

    const CUTOFF_MS = 60 * 60 * 1000;
    const sanitize = (arr) =>
        Array.isArray(arr)
            ? arr.filter((e) => e && typeof e.timestamp === "number" && e.timestamp >= Date.now() - CUTOFF_MS)
            : [];

    const readSnapshot = (hotelRef) => {
        try {
            const raw = localStorage.getItem(SNAPSHOT_KEY(hotelRef));
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    };
    const writeSnapshot = (hotelRef, payload) => {
        try {
            localStorage.setItem(SNAPSHOT_KEY(hotelRef), JSON.stringify(payload));
        } catch {}
    };
    const clearSnapshot = (hotelRef) => {
        try {
            localStorage.removeItem(SNAPSHOT_KEY(hotelRef));
        } catch {}
    };
    const writeTombstone = (hotelRef) => {
        try {
            localStorage.setItem(TOMBSTONE_KEY(hotelRef), String(Date.now()));
        } catch {}
    };
    const readTombstone = (hotelRef) => {
        try {
            return Number(localStorage.getItem(TOMBSTONE_KEY(hotelRef)) || "0") || 0;
        } catch {
            return 0;
        }
    };
    const canRestore = (snap, hotelRef) => {
        const tomb = readTombstone(hotelRef);
        const savedAt = Number(snap?.savedAt) || 0;
        return tomb <= 0 || savedAt > tomb;
    };

    const saveSnapshot = () => {
        if (Date.now() - flushedAtRef.current < 4000) return;
        const inst = statsRef.current;
        if (!inst) return;
        const tomb = readTombstone(projectRefRef.current);
        if (Date.now() - tomb < 10000) return;
        const durationEvents = sanitize(inst._durationEvents);
        const eventStack = sanitize(inst._eventStack);
        if (!durationEvents.length && !eventStack.length) {
            clearSnapshot(projectRefRef.current);
            return;
        }
        writeSnapshot(projectRefRef.current, {
            hotelRef: projectRefRef.current,
            durationEvents,
            eventStack,
            savedAt: Date.now(),
            version: 1,
        });
    };

    const FLUSH_WINDOW_MS = 3000;
    const lastFlushAtRef = useRef(0);

    useEffect(() => {
        if (!servicePath) {
            setStats(null);
            return;
        }

        const instanceId = makeId();
        const instance = new StatsCollector({
            deviceRef,
            servicePath,
            roomId: selectedRoomId,
            token,
            reportLocation: STATS_LOCATION.APP,
            deviceType: STATS_DEVICE_TYPE.TV,
            stayGuestId: guestId,
            source: STATS_SOURCE.MOBILE,
            flushInterval: 40,
            minimumDurationEventTime: 5,
            projectRef,
            onFlush: (ok) => {
                const now = Date.now();
                if (now - lastFlushAtRef.current < FLUSH_WINDOW_MS) {
                    console.log(`[DIAG zstats:${instanceId}] onFlush (IGNORED window) ok=${ok} at ${nowStr()}`);
                    return;
                }
                lastFlushAtRef.current = now;

                console.log(`[DIAG zstats:${instanceId}] onFlush ok=${ok} at ${nowStr()}`);
                flushedAtRef.current = Date.now();
                writeTombstone(projectRefRef.current);
                clearSnapshot(projectRefRef.current);
            },
        });

        const existing = readAndConsumeSessionId(projectRef);
        if (existing) {
            instance.sessionId = existing;
        }
        instanceIdRef.current = instanceId;
        console.log(`[DIAG zstats:${instanceId}] CREATED`, {
            selectedRoom,
            projectRef,
            servicePath,
            hasToken: !!token,
            at: nowStr(),
        });

        const snap = readSnapshot(projectRef);
        if (snap?.hotelRef === projectRef && canRestore(snap, projectRef)) {
            if (snap.durationEvents && typeof snap.durationEvents === "object") {
                instance._durationEvents = snap.durationEvents;
            }
            if (Array.isArray(snap.eventStack)) {
                instance._eventStack = snap.eventStack;
            }
        }

        statsRef.current = instance;
        setStats(instance);

        return () => {
            console.log(`[DIAG zstats:${instanceId}] DESTROYED ${nowStr()}`);
            try {
                instance.stop?.();
            } catch {}
            if (statsRef.current === instance) statsRef.current = null;
        };
    }, [servicePath, projectRef]);

    useEffect(() => {
        const inst = statsRef.current;
        if (!inst) return;
        const newToken = userToken || appToken;
        if (newToken && inst.token !== newToken) {
            inst.token = newToken;
        }
    }, [userToken, appToken]);

    useEffect(() => {
        const inst = statsRef.current;
        if (!inst) return;

        if (inst.roomId !== selectedRoomId) {
            inst.roomId = selectedRoomId;
        }
    }, [selectedRoomId]);

    useEffect(() => {
        const onVisibilityChange = () => {
            console.log(`[DIAG] visibility=${document.visibilityState} at ${nowStr()}`);
            if (document.visibilityState === "hidden") saveSnapshot();
        };
        const onPageHide = () => {
            console.log(`[DIAG] pagehide at ${nowStr()}`);
            saveSnapshot();
        };
        const onBeforeUnload = () => {
            console.log(`[DIAG] beforeunload at ${nowStr()}`);
            saveSnapshot();
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, []);

    return (
        <StatsContext.Provider value={stats ? Object.assign(stats, { saveSnapshot }) : null}>
            {children}
        </StatsContext.Provider>
    );
}

export function useStats() {
    return useContext(StatsContext);
}
